import Tooltip from 'src/app/project/features/tooltip/Tooltip';
import { ECustomFieldType } from 'src/app/project/modules/custom-fields/custom-field-types-enums';
import { GET_CUSTOM_FIELDS } from 'src/app/project/modules/custom-fields/custom-fields.store';
import { TPoint } from 'src/app/project/modules/points/points.model';
import { TPreferences } from 'src/app/project/modules/preferences/preferences.model';
import { GET_PREFERENCES } from 'src/app/project/modules/preferences/preferences.store';
import { canEditPoint } from 'src/app/project/modules/share/share-utils/share-permissions';
import { GET_SHARES } from 'src/app/project/modules/share/shares.store';
import { transformDate } from 'src/app/project/shared/date-transformer';
import { GET_TIMELINE_COLUMNS } from '../../timeframes/timeline-timeframes';
import Timeline from '../../Timeline';
import { GET_TIMELINE } from '../../timeline.ui.store';
import { createTimelineBasicElement } from './create-timeline-basic-element';
import { createTimelineCustomFieldTooltip } from './create-timeline-custom-field-tooltip';
import { createTimelineEmptyElement } from './create-timeline-empty-element';
import { createTimelineInnerElement } from './create-timeline-inner-element';
import { createTimelinePriorityElement } from './create-timeline-priority-element';
import { createTimelineResizeHandleLeft } from './create-timeline-resize-handle-left';
import { createTimelineResizeHandleRight } from './create-timeline-resize-handle-right';
import { getTimeframeIndex } from './get-timeframe-index';

export function createTimelineCustomFieldElement(pointIndex: number): HTMLElement {
  const timeline = GET_TIMELINE();
  const data = GET_TIMELINE_COLUMNS();
  const shares = GET_SHARES();

  const point = timeline.getPoints()[pointIndex];
  let timelineFieldId: string = getCustomFieldId(point);

  if (!timelineFieldId) {
    return createTimelineEmptyElement(pointIndex);
  }

  const timelineField = point.customFieldsMap[timelineFieldId];

  if (!timelineField.value) {
    return createTimelineEmptyElement(pointIndex);
  }

  const preferences = GET_PREFERENCES();
  const values: string[] = timelineField.value.split('~');

  const startDateIndex = getTimeframeIndex(data.formattedTimeframes, +values[0]);
  const endDateIndex = getTimeframeIndex(data.formattedTimeframes, +values[1]);

  const numberOfColumnsToAdd = endDateIndex - startDateIndex + 1; // +1 so it doesn't end at the start of end day and fills that column
  const width = data.bottomWidth * numberOfColumnsToAdd;
  const share = shares.find((_share) => _share.workspaceId === point.workspaceRef.id);
  const canEdit = canEditPoint(share.shareOption);
  const timelineGroup = preferences.timeline?.group ? preferences.timeline.group : null;
  const fieldTitle = `${point.sequenceNumber} - ${point.title}`;

  const element = createTimelineBasicElement({
    width,
    data,
    pointIndex,
    startDateIndex,
    timelineGroup,
    timelineField,
    canEdit,
  });

  const tooltip = createDateTooltip(values, preferences, element, timeline);

  element.appendChild(
    createTimelineResizeHandleLeft(pointIndex, width, timelineField, canEdit, element),
  );
  element.appendChild(createTimelinePriorityElement(point.status, point.priority));
  element.appendChild(createTimelineInnerElement(fieldTitle, width, tooltip, point));
  element.appendChild(
    createTimelineResizeHandleRight(pointIndex, width, timelineField, canEdit, element),
  );

  return element;
}

function getCustomFieldId(point: TPoint): string {
  let timelineFieldId: string;

  if (point?.customFieldsMap) {
    timelineFieldId = Object.keys(point.customFieldsMap).find((_customFieldId) => {
      const customField = GET_CUSTOM_FIELDS()[_customFieldId];

      return customField.type === ECustomFieldType.TIMELINE;
    });
  }
  return timelineFieldId;
}

function createDateTooltip(
  values: string[],
  preferences: TPreferences,
  element: HTMLElement,
  timeline: Timeline,
): Tooltip {
  const date =
    transformDate({
      value: new Date(+values[0]),
      inputHourFormat: '',
      format: preferences.dateFormat,
      localTime: false,
    }) +
    ' - ' +
    transformDate({
      value: new Date(+values[1]),
      inputHourFormat: '',
      format: preferences.dateFormat,
      localTime: false,
    });

  return createTimelineCustomFieldTooltip(date, element, timeline);
}
